/**
 * RecommendationCarousel Component
 * 
 * Displays AI-recommended entities in a horizontal scrollable carousel.
 * Features:
 * - Relevance score badges with color-coded indicators
 * - Human-readable reason labels
 * - Entity metadata display (industry, region, custom fields)
 * - Loading and error states
 * - Keyboard navigation and WCAG AA accessibility
 * - Dark mode support
 * - Click handlers for entity selection
 */

import React, { useCallback, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, AlertCircle, Loader } from 'lucide-react';
import { useRecommendations } from '@/hooks/useRecommendations';

interface RecommendationCarouselProps {
  entityId: string;
  entityType: 'client' | 'partner' | 'solution';
  limit?: number;
  onSelect?: (entityId: string, name: string) => void;
  className?: string;
  excludeIds?: string[];
}

interface RecommendationItem {
  entity_id: string;
  name: string;
  type: string;
  reason: string;
  relevance_score: number;
  metadata: Record<string, any>;
}

/**
 * Relevance Score Badge Component
 * Color-coded visual indicator of recommendation strength
 */
const RelevanceScoreBadge: React.FC<{ score: number }> = ({ score }) => {
  let bgColor = 'bg-gray-200 dark:bg-gray-700';
  let textColor = 'text-gray-900 dark:text-gray-100';
  let label = 'Low';

  if (score >= 0.8) {
    bgColor = 'bg-green-100 dark:bg-green-900/40';
    textColor = 'text-green-700 dark:text-green-300';
    label = 'Strong Match';
  } else if (score >= 0.6) {
    bgColor = 'bg-blue-100 dark:bg-blue-900/40';
    textColor = 'text-blue-700 dark:text-blue-300';
    label = 'Good Match';
  } else if (score >= 0.4) {
    bgColor = 'bg-yellow-100 dark:bg-yellow-900/40';
    textColor = 'text-yellow-700 dark:text-yellow-300';
    label = 'Fair Match';
  }

  const percentage = Math.round(score * 100);

  return (
    <div
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${bgColor} ${textColor}`}
      role="img"
      aria-label={`Relevance: ${label} (${percentage}%)`}
    >
      <span className="mr-1">↑</span>
      {percentage}%
    </div>
  );
};

/**
 * Recommendation Card Component
 * Single recommendation item with metadata display
 */
const RecommendationCard: React.FC<{
  item: RecommendationItem;
  onSelect: (entityId: string, name: string) => void;
}> = ({ item, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect(item.entity_id, item.name);
  }, [item.entity_id, item.name, onSelect]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className="flex-shrink-0 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-500 transition-colors shadow-sm hover:shadow-md cursor-pointer"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-pressed="false"
      aria-label={`${item.name} - ${item.reason} - ${Math.round(item.relevance_score * 100)}% relevance`}
    >
      {/* Header: Name and Relevance Badge */}
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {item.name}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
          </p>
        </div>
        <RelevanceScoreBadge score={item.relevance_score} />
      </div>

      {/* Reason: Why this is recommended */}
      <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-100 dark:border-gray-600">
        <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
          <span className="font-semibold text-gray-900 dark:text-gray-100">Why: </span>
          {item.reason}
        </p>
      </div>

      {/* Metadata: Industry, Region, Custom Fields */}
      {Object.keys(item.metadata).length > 0 && (
        <div className="space-y-2 text-xs">
          {item.metadata.industry && (
            <div className="flex items-start gap-2">
              <span className="text-gray-500 dark:text-gray-400">🏢</span>
              <span className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold text-gray-900 dark:text-gray-100">Industry:</span>{' '}
                {item.metadata.industry}
              </span>
            </div>
          )}
          {item.metadata.region && (
            <div className="flex items-start gap-2">
              <span className="text-gray-500 dark:text-gray-400">📍</span>
              <span className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold text-gray-900 dark:text-gray-100">Region:</span>{' '}
                {item.metadata.region}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Footer: CTA */}
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <p className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
          Click to select →
        </p>
      </div>
    </div>
  );
};

/**
 * RecommendationCarousel Component
 * Main carousel for displaying recommendations
 */
export const RecommendationCarousel: React.FC<RecommendationCarouselProps> = ({
  entityId,
  entityType,
  limit = 5,
  onSelect,
  className = '',
  excludeIds = [],
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Fetch recommendations using custom hook
  const { recommendations, loading, error, refetch } = useRecommendations({
    entityId,
    entityType,
    limit,
    excludeIds,
  });

  // Handle scroll position to show/hide navigation arrows
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 400;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleSelect = useCallback(
    (entityId: string, name: string) => {
      onSelect?.(entityId, name);
    },
    [onSelect]
  );

  // Loading State
  if (loading) {
    return (
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg p-6 ${className}`}
        role="status"
        aria-live="polite"
        aria-label="Loading recommendations"
      >
        <div className="flex items-center justify-center gap-3">
          <Loader className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Finding related {entityType}s...
          </p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div
        className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 ${className}`}
        role="alert"
        aria-live="assertive"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-red-900 dark:text-red-300">
              Could not load recommendations
            </h4>
            <p className="text-xs text-red-700 dark:text-red-400 mt-1">
              {error}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-2 text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
              aria-label="Retry loading recommendations"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty State
  if (!recommendations || recommendations.length === 0) {
    return (
      <div
        className={`bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center ${className}`}
        role="status"
        aria-label="No recommendations available"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No related {entityType}s found. Try searching elsewhere.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`relative group ${className}`}
      role="region"
      aria-label={`Recommended ${entityType}s carousel with ${recommendations.length} items`}
    >
      {/* Title */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          ✨ Recommended for you
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Based on similarity and ecosystem relationships
        </p>
      </div>

      {/* Carousel Container */}
      <div className="relative">
        {/* Left Navigation Arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-2 shadow-lg hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-500 transition-all"
            aria-label="Scroll recommendations left"
            title="Scroll left"
          >
            <ChevronLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
        )}

        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="overflow-x-auto scrollbar-hide flex gap-4 pb-2"
          role="presentation"
        >
          {recommendations.map((item) => (
            <RecommendationCard
              key={item.entity_id}
              item={item}
              onSelect={handleSelect}
            />
          ))}
        </div>

        {/* Right Navigation Arrow */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-2 shadow-lg hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-500 transition-all"
            aria-label="Scroll recommendations right"
            title="Scroll right"
          >
            <ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
        )}
      </div>

      {/* Scroll Indicator */}
      <div className="mt-2 text-center">
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {recommendations.length} result{recommendations.length !== 1 ? 's' : ''} • Scroll to see more
        </p>
      </div>
    </div>
  );
};

export default RecommendationCarousel;
